import * as React from 'react';

import {Box, Stack, Typography} from '@mui/material';
import {AggregationSurveyHealthSeries} from 'crqlar-react-common/dist/models/aggregation/surveyAggregation.model';
import {useTranslation} from 'react-i18next';

import {CardHeadline} from '../../../insights/kit/Headline';
import {CHART_HEIGHT} from "../../../insights/kit/insightsChart";
import {NEGATIVE_COLOR} from "../../../insights/kit/insightsColors";
import {SeriesCard} from '../../../insights/kit/SeriesCard';
import {useInsightsFormatters, verdictColor} from "../../../insights/kit/insightsFormat";
import {InsightsLoading} from '../../../insights/kit/insightsLoading';
import {InsightsColumn, InsightsTable} from '../../../insights/kit/InsightsTable';
import {
    stepVerdict,
    SurveyHealthChart,
    SurveyHealthLabels,
    SurveyHealthLegend,
    SurveyHealthSpec,
    SurveyStepPoint,
    SurveyStepRow,
} from '../charts/SurveyHealthChart';

interface SurveyHealthCardProps {
    series?: AggregationSurveyHealthSeries;
    loading?: InsightsLoading;
    height?: number;
}

/**
 * A question added in a later version was never put to the guests who filled out an earlier one,
 * so the first step is the wrong denominator for it - it would draw "1 of 1" as half the survey.
 * The step's own `eligible` is the population that could have reached it; the first step only
 * stands in for it on an API that does not send one.
 */
const survivalOf = (reached: number, eligible: number | null, started: number) => {
    const base = eligible ?? started;

    return base === 0 ? 0 : (reached / base) * 100;
};

function toSurveyStepRows(data: SurveyStepPoint[], compareData?: SurveyStepPoint[]): SurveyStepRow[] {
    const started = data[0]?.reached ?? 0;
    const compareStarted = compareData?.[0]?.reached ?? 0;

    return data.map((point, index) => {
        const comparePoint = compareData?.[index];
        const compareReached = comparePoint?.reached ?? null;

        return {
            ...point,
            dropRate: point.reached === 0 ? 0 : (point.dropped / point.reached) * 100,
            survival: survivalOf(point.reached, point.eligible, started),
            compareReached,
            compareEligible: comparePoint?.eligible ?? null,
            compareSurvival: compareReached == null
                ? null
                : survivalOf(compareReached, comparePoint?.eligible ?? null, compareStarted),
        };
    });
}

const DROP_OFF_COUNT = 3;

/** What the chart gives up while the list shows, so the card stays the height of its neighbour. */
const DROP_OFF_LIST_ROOM = 110;

interface DropOffListProps {
    rows: SurveyStepRow[];
    title: string;
    formatPercent: (value: number) => string;
}

/** The questions that lost the most guests, by name: with 30 or 40 bars, "Q17" alone says nothing. */
function DropOffList({rows, title, formatPercent}: DropOffListProps) {
    return (
        <Stack sx={{mt: 2}}>
            <Typography sx={{fontSize: 12, color: 'text.secondary', mb: 0.5}}>{title}</Typography>

            {rows.map(row => (
                <Stack
                    key={row.step}
                    direction={'row'}
                    alignItems={'center'}
                    gap={1.5}
                    sx={{py: 0.75, minWidth: 0, borderTop: '1px solid', borderColor: 'divider'}}
                >
                    <Typography sx={{width: 36, flexShrink: 0, fontSize: 13, color: 'text.secondary', fontVariantNumeric: 'tabular-nums'}}>
                        {row.label}
                    </Typography>

                    <Typography noWrap title={row.question} sx={{flex: 1, minWidth: 0, fontSize: 13, color: 'text.primary'}}>
                        {row.question}
                    </Typography>

                    <Typography sx={{flexShrink: 0, fontSize: 13, fontWeight: 600, color: NEGATIVE_COLOR, fontVariantNumeric: 'tabular-nums'}}>
                        {`−${formatPercent(row.dropRate)}`}
                    </Typography>
                </Stack>
            ))}
        </Stack>
    );
}

interface TableLabels extends SurveyHealthLabels {
    step: string;
}

interface StepTableProps {
    rows: ReadonlyArray<SurveyStepRow>;
    labels: TableLabels;
    hasCompare: boolean;
    target?: number;
    formatCount: (value: number) => string;
    formatPercent: (value: number) => string;
    worstStep: number | null;
}

function StepTable({rows, labels, hasCompare, target, formatCount, formatPercent, worstStep}: StepTableProps) {
    const columns: Array<InsightsColumn<SurveyStepRow> | false> = [
        {
            key: 'step',
            kind: 'label',
            label: labels.step,
            cell: row => (
                <Box component={'span'} title={row.question}>
                    <Box component={'span'} sx={{color: 'text.secondary', mr: 1}}>{row.label}</Box>
                    {row.question}
                </Box>
            ),
        },
        {key: 'reached', kind: 'value', label: labels.reached, cell: row => row.reached, format: formatCount},
        hasCompare && {key: 'compareReached', kind: 'compare', label: labels.compareReached, cell: row => row.compareReached, format: formatCount},
        {key: 'dropped', kind: 'detail', label: labels.dropped, cell: row => row.dropped, format: formatCount},
        {
            key: 'dropRate',
            kind: 'detail',
            label: labels.dropRate,
            cell: row => row.dropRate,
            format: formatPercent,
            // The steepest drop is worth pointing at, but nothing set a bar for it to have missed -
            // so it is emphasis against the column's muted rest, not a verdict.
            color: row => (row.step === worstStep ? 'text.primary' : undefined),
        },
        {
            key: 'survival',
            kind: 'verdict',
            label: labels.survival,
            cell: row => row.survival,
            format: formatPercent,
            color: row => verdictColor(stepVerdict(row, target)),
        },
    ];

    return <InsightsTable label={labels.step} columns={columns} rows={rows} rowKey={row => row.step}/>;
}

export function SurveyHealthCard({series, loading, height = CHART_HEIGHT}: SurveyHealthCardProps) {
    const {t, i18n} = useTranslation();

    const {data, compareData} = React.useMemo(() => {
        const steps = series?.steps ?? [];

        const localized = (text: Record<string, string> | null) =>
            text == null ? '' : text[i18n.language] ?? text['en'] ?? Object.values(text)[0] ?? '';

        const point = (
            step: AggregationSurveyHealthSeries['steps'][number],
            reached: number,
            dropped: number,
            eligible: number | null
        ): SurveyStepPoint => ({
            step: step.index + 1,
            label: `Q${step.index + 1}`,
            question: localized(step.question),
            reached,
            dropped,
            eligible,
        });

        const hasCompare = steps.some(step => step.compareReached != null);

        return {
            data: steps.map(step => point(step, step.reached, step.dropped, step.eligible ?? null)),
            compareData: hasCompare
                ? steps.map(step => point(
                    step,
                    step.compareReached ?? 0,
                    step.compareDropped ?? 0,
                    step.compareEligible ?? null
                ))
                : undefined,
        };
    }, [i18n.language, series]);

    const rows = React.useMemo(() => toSurveyStepRows(data, compareData), [compareData, data]);

    const hasCompare = compareData != null;
    const target = series?.summary.target ?? undefined;

    const completion = series?.summary.completion ?? null;
    const previousCompletion = series?.summary.previousCompletion ?? null;
    const completionDelta = completion == null || previousCompletion == null ? null : completion - previousCompletion;

    const dropOffs = React.useMemo(
        () => rows
            .filter(row => row.dropped > 0)
            .sort((left, right) => right.dropRate - left.dropRate || right.dropped - left.dropped || left.step - right.step)
            .slice(0, DROP_OFF_COUNT),
        [rows],
    );
    const marked = React.useMemo(() => new Set(dropOffs.map(row => row.step)), [dropOffs]);

    const worstStep = React.useMemo<number | null>(() => {
        let worst: SurveyStepRow | null = null;

        for (const row of rows) {
            if (worst == null || row.dropRate > worst.dropRate) worst = row;
        }

        return worst?.step ?? null;
    }, [rows]);

    const format = useInsightsFormatters();
    const formatCount = format.count;
    const formatPercent = React.useCallback((value: number) => `${format.decimal(value)} %`, [format]);
    const formatPoints = format.decimal;

    const labels = React.useMemo<SurveyHealthLabels>(
        () => ({
            reached: t('Reached'),
            dropped: t('Dropped'),
            dropRate: t('Drop-off'),
            survival: t('Still filling out'),
            compareReached: t('Compare period'),
            target: t('Target'),
            aboveTarget: t('At or above target'),
            belowTarget: t('Below target'),
        }),
        [t],
    );

    const tableLabels = React.useMemo<TableLabels>(() => ({step: t('Question'), ...labels}), [labels, t]);

    const headline = (
        <CardHeadline
            value={completion == null ? '—' : formatPoints(completion)}
            change={previousCompletion == null ? null : {
                title: t('Completion'),
                current: completion == null ? '—' : formatPoints(completion),
                previous: formatPoints(previousCompletion),
                delta: completionDelta,
                formatDelta: formatPoints,
            }}
        />
    );

    const spec: SurveyHealthSpec = {rows, target, labels, formatCount, formatPercent};

    return (
        <SeriesCard
            title={t('Survey health')}
            hint={t('Completion is the share of everyone who started the survey and reached the end. Each bar is the share still filling out at that question, and the count it stands for; the list adds how many people each question lost.')}
            headline={headline}
            loading={loading}
            legend={<SurveyHealthLegend {...spec}/>}
            empty={rows.length === 0 ? (
                <Typography variant={'body2'} color={'text.secondary'}>
                    {t('No responses in this period.')}
                </Typography>
            ) : undefined}
            chart={(
                <React.Fragment>
                    <SurveyHealthChart
                        {...spec}
                        height={dropOffs.length > 0 ? height - DROP_OFF_LIST_ROOM : height}
                        marked={marked}
                    />

                    {dropOffs.length > 0 && (
                        <DropOffList rows={dropOffs} title={t('Biggest drop-offs')} formatPercent={formatPercent}/>
                    )}
                </React.Fragment>
            )}
            table={(
                <StepTable
                    rows={rows}
                    labels={tableLabels}
                    hasCompare={hasCompare}
                    target={target}
                    formatCount={formatCount}
                    formatPercent={formatPercent}
                    worstStep={worstStep}
                />
            )}
        />
    );
}
